import { useEffect, useState, useMemo } from 'react';
import {
  Card,
  Form,
  Select,
  DatePicker,
  Input,
  Switch,
  Button,
  Table,
  Modal,
  Popconfirm,
  Space,
  Tag,
  Typography,
  Empty,
  Alert,
  Divider,
  Row,
  Col,
} from 'antd';
import { CalendarOutlined, FlagOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  FollowUpStatus,
  FinalOutcome,
  type CreateFollowUpVisitDto,
  type CreatePatientOutcomeDto,
  type FollowUpVisit,
  type PatientOutcome,
} from '../../../../api/dermatoApi';
import { useAuthStore } from '../../../../store/authStore';
import { useDermatoWizard, useRegisterStepSave } from '../DermatoWizardContext';
import dermatoApi from '../../../../api/dermatoApi';

const { Text } = Typography;

const FOLLOWUP_STATUS_OPTIONS = Object.values(FollowUpStatus).map((v) => ({
  value: v,
  label:
    v === 'FAVORABLE' ? 'Favorabil'
    : v === 'UNCHANGED' ? 'Neschimbat'
    : v === 'STABLE_DISEASE' ? 'Boală stabilă'
    : v === 'RECURRENCE' ? 'Recidivă'
    : 'Progresie',
}));

const OUTCOME_OPTIONS = Object.values(FinalOutcome).map((v) => ({
  value: v,
  label:
    v === 'NO_EVIDENCE_OF_DISEASE' ? 'Fără semne de boală'
    : v === 'STABLE_DISEASE' ? 'Boală stabilă'
    : v === 'PROGRESSION' ? 'Progresie'
    : v === 'RECURRENCE' ? 'Recidivă'
    : v === 'DECEASED' ? 'Decedat'
    : v === 'LOST_TO_FOLLOWUP' ? 'Pierdut din evidență'
    : 'În tratament',
}));

const Step15FollowUpOutcome = () => {
  const { lesions, selectedLesionId, selectLesion, loading } = useDermatoWizard();
  const { user } = useAuthStore();

  const [visits, setVisits] = useState<FollowUpVisit[]>([]);
  const [outcome, setOutcome] = useState<PatientOutcome | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [savingVisit, setSavingVisit] = useState(false);
  const [savingOutcome, setSavingOutcome] = useState(false);
  const [visitModalOpen, setVisitModalOpen] = useState(false);
  const [editingVisit, setEditingVisit] = useState<FollowUpVisit | null>(null);
  const [visitForm] = Form.useForm<CreateFollowUpVisitDto>();
  const [outcomeForm] = Form.useForm<CreatePatientOutcomeDto>();

  const lesion = useMemo(
    () => lesions.find((l) => l.id === selectedLesionId) ?? null,
    [lesions, selectedLesionId]
  );

  const refresh = async () => {
    if (!lesion?.id) return;
    setLoadingList(true);
    try {
      const [v, o] = await Promise.all([
        dermatoApi.followUps.getByLesion(lesion.id),
        dermatoApi.outcomes.getByLesion(lesion.id),
      ]);
      setVisits(v);
      setOutcome(o);
      if (o) {
        outcomeForm.setFieldsValue({
          ...o,
          evaluationDate: dayjs(o.evaluationDate),
          lastContactDate: o.lastContactDate ? dayjs(o.lastContactDate) : undefined,
        } as any);
      } else {
        outcomeForm.resetFields();
        outcomeForm.setFieldsValue({
          evaluationDate: dayjs(),
          status: FinalOutcome.NO_EVIDENCE_OF_DISEASE,
          inFollowUp: true,
          evaluatedBy: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || user?.email || '',
        } as any);
      }
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesion?.id]);

  const openCreateVisit = () => {
    setEditingVisit(null);
    visitForm.resetFields();
    visitForm.setFieldsValue({
      visitDate: dayjs(),
      status: FollowUpStatus.FAVORABLE,
      performedBy: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || user?.email || '',
      recurrence: false,
      newLesions: false,
    } as any);
    setVisitModalOpen(true);
  };

  const openEditVisit = (v: FollowUpVisit) => {
    setEditingVisit(v);
    visitForm.setFieldsValue({
      ...v,
      visitDate: dayjs(v.visitDate),
      nextVisitDate: v.nextVisitDate ? dayjs(v.nextVisitDate) : undefined,
    } as any);
    setVisitModalOpen(true);
  };

  const handleSaveVisit = async (): Promise<boolean> => {
    if (!lesion?.id) return false;
    try {
      const values = await visitForm.validateFields();
      setSavingVisit(true);
      const dto: CreateFollowUpVisitDto = {
        ...values,
        visitDate: (values.visitDate as unknown as dayjs.Dayjs).toISOString(),
        nextVisitDate: values.nextVisitDate ? (values.nextVisitDate as unknown as dayjs.Dayjs).toISOString() : undefined,
      };
      if (editingVisit?.id) {
        await dermatoApi.followUps.update(editingVisit.id, dto);
      } else {
        await dermatoApi.followUps.createForLesion(lesion.id, dto);
      }
      setVisitModalOpen(false);
      await refresh();
      return true;
    } catch {
      // validation
      return false;
    } finally {
      setSavingVisit(false);
    }
  };

  const handleDeleteVisit = async (id: string) => {
    await dermatoApi.followUps.delete(id);
    await refresh();
  };

  const handleSaveOutcome = async (): Promise<boolean> => {
    if (!lesion?.id) return true;
    try {
      const values = await outcomeForm.validateFields();
      setSavingOutcome(true);
      const dto: CreatePatientOutcomeDto = {
        ...values,
        evaluationDate: (values.evaluationDate as unknown as dayjs.Dayjs).toISOString(),
        lastContactDate: values.lastContactDate ? (values.lastContactDate as unknown as dayjs.Dayjs).toISOString() : undefined,
      };
      const saved = outcome?.id
        ? await dermatoApi.outcomes.update(outcome.id, dto)
        : await dermatoApi.outcomes.createForLesion(lesion.id, dto);
      setOutcome(saved);
      return true;
    } catch {
      // validation
      return false;
    } finally {
      setSavingOutcome(false);
    }
  };

  // Cu modalul de vizită deschis salvăm vizita; altfel salvăm outcome-ul (vizibil permanent).
  const saveCurrentStep = async (): Promise<boolean> => {
    if (visitModalOpen) return handleSaveVisit();
    return handleSaveOutcome();
  };

  useRegisterStepSave(saveCurrentStep);

  if (loading) return <Card loading title="Follow-up / Outcome" />;

  if (lesions.length === 0) {
    return (
      <Card title="Follow-up / Outcome">
        <Empty description="Nu există leziuni. Mergi la pasul 3." />
      </Card>
    );
  }

  return (
    <>
      <Card
        title={
          <Space>
            <CalendarOutlined style={{ color: '#1890ff' }} />
            <span>Follow-up + Outcome — per leziune</span>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <Form.Item label="Selectează leziunea" required style={{ marginBottom: 16 }}>
          <Select
            value={selectedLesionId ?? undefined}
            onChange={selectLesion}
            placeholder="Alege o leziune"
            options={lesions.map((l) => ({
              value: l.id,
              label: `${l.clinicalCode} — ${l.location}`,
            }))}
          />
        </Form.Item>

        {!lesion && <Alert type="warning" showIcon message="Selectează o leziune." />}

        {lesion && loadingList && <Alert type="info" message="Se încarcă vizitele + outcome-ul…" />}
      </Card>

      {lesion && !loadingList && (
        <Row gutter={16}>
          <Col xs={24} md={14}>
            <Card
              title={
                <Space>
                  📅 Vizite follow-up
                  <Tag>{visits.length}</Tag>
                </Space>
              }
              extra={
                <Button type="primary" icon={<PlusOutlined />} onClick={openCreateVisit}>
                  Adaugă vizită
                </Button>
              }
            >
              {visits.length === 0 ? (
                <Empty description="Nicio vizită de follow-up înregistrată." />
              ) : (
                <Table
                  scroll={{ x: 'max-content' }}
                  rowKey="id"
                  dataSource={visits}
                  size="small"
                  pagination={{ pageSize: 5 }}
                  columns={[
                    { title: 'Data', dataIndex: 'visitDate', render: (v: string) => new Date(v).toLocaleDateString('ro-RO') },
                    { title: 'Status', dataIndex: 'status', render: (v: string) => <Tag>{v}</Tag> },
                    { title: 'Recidivă', dataIndex: 'recurrence', render: (v: boolean) => (v ? <Tag color="red">DA</Tag> : <Tag>NU</Tag>) },
                    { title: 'Leziuni noi', dataIndex: 'newLesions', render: (v: boolean) => (v ? <Tag color="orange">DA</Tag> : <Tag>NU</Tag>) },
                    {
                      title: 'Următoarea',
                      dataIndex: 'nextVisitDate',
                      render: (v: string | null) => (v ? new Date(v).toLocaleDateString('ro-RO') : '—'),
                    },
                    { title: 'Operator', dataIndex: 'performedBy' },
                    {
                      title: '',
                      width: 100,
                      render: (_: unknown, v: FollowUpVisit) => (
                        <Space size="small">
                          <Button size="small" icon={<EditOutlined />} onClick={() => openEditVisit(v)} />
                          <Popconfirm title="Șterge vizită?" onConfirm={() => void handleDeleteVisit(v.id)}>
                            <Button size="small" danger icon={<DeleteOutlined />} />
                          </Popconfirm>
                        </Space>
                      ),
                    },
                  ]}
                />
              )}
            </Card>
          </Col>

          <Col xs={24} md={10}>
            <Card
              title={
                <Space>
                  <FlagOutlined style={{ color: '#1890ff' }} />
                  <span>Outcome final</span>
                </Space>
              }
            >
              <Form form={outcomeForm} layout="vertical">
                <Form.Item name="evaluationDate" label="Data evaluării" rules={[{ required: true }]}>
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="status" label="Status final" rules={[{ required: true }]}>
                  <Select options={OUTCOME_OPTIONS} />
                </Form.Item>
                <Form.Item name="lastContactDate" label="Data ultimului contact">
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="inFollowUp" label="Încă în follow-up" valuePropName="checked">
                  <Switch />
                </Form.Item>
                <Form.Item name="evaluatedBy" label="Evaluat de" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
                <Form.Item name="observations" label="Observații">
                  <Input.TextArea rows={3} />
                </Form.Item>
                <Divider style={{ margin: '8px 0' }} />
                <Form.Item>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Button type="primary" onClick={() => void handleSaveOutcome()} loading={savingOutcome} block>
                      {outcome ? 'Actualizează outcome' : 'Salvează outcome'}
                    </Button>
                    {outcome && <Text type="success">✓ Outcome existent — {outcome.id.slice(0, 8)}</Text>}
                  </Space>
                </Form.Item>
              </Form>
            </Card>
          </Col>
        </Row>
      )}

      <Modal
        title={editingVisit ? 'Editare vizită follow-up' : 'Vizită follow-up nouă'}
        open={visitModalOpen}
        onCancel={() => setVisitModalOpen(false)}
        onOk={() => void handleSaveVisit()}
        confirmLoading={savingVisit}
        okText={editingVisit ? 'Salvează' : 'Creează'}
        cancelText="Anulează"
        destroyOnClose
      >
        <Form form={visitForm} layout="vertical" preserve={false}>
          <Form.Item name="visitDate" label="Data vizitei" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select options={FOLLOWUP_STATUS_OPTIONS} />
          </Form.Item>
          <Form.Item name="recurrence" label="Recidivă" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="newLesions" label="Leziuni noi" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="nextVisitDate" label="Următoarea vizită">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="actions" label="Acțiuni / Observații">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="performedBy" label="Operator" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default Step15FollowUpOutcome;
